import { Injectable } from '@angular/core';
import 'rxjs/add/observable/fromPromise';
import { EtherInterfaceService } from '@app/core/ether-interface.service';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { Logger } from '@app/core/logger.service';

const logger = new Logger('@core:state-channel.service.ts');

export class HashAndSign {
  msgHash: string;
  sign: any;

  constructor() {
    this.msgHash = null;
    this.sign = null;
  }
}

export class StateChannel {
  winBalance: number;
  gameCount: number;

  constructor() {
    this.winBalance = 0;
    this.gameCount = 0;
  }
}

@Injectable()
export class StateChannelService {
  public gameCount: number = 0;
  public winBalance: number = 0;
  private _lastBet: number = 0;

  /**
   * @param {AuthenticationService} auth
   * @param {EtherInterfaceService} eth
   */
  constructor(
    private auth: AuthenticationService, // only needed because of ethereum wallet private key
    private eth: EtherInterfaceService
  ) {
    logger.info('service constructed');
  }

  /**
   * Set new state-channel data.
   * @param {number} winBalance
   * @param {number} gameCount
   */
  public init(winBalance: number, gameCount: number): void {
    if (!this.auth.isMainServer()) {
      return;
    }
    this.winBalance = winBalance;
    this.gameCount = gameCount;
    // logger.info('init', this.winBalance, this.gameCount);
  }

  /**
   * Get state channel data.
   * @returns {StateChannel}
   */
  public get(): StateChannel {
    logger.info('get', this.winBalance, this.gameCount);
    return {
      winBalance: this.winBalance,
      gameCount: this.gameCount,
    };
  }

  /**
   * Adds bet to local state channel data and returns msgHash with signature.
   * @param {number} amount
   * @param {Buffer} pKey
   * @returns {HashAndSign}
   */
  public addBet(amount: number, pKey: any = null): HashAndSign {
    if (this.auth.isTestServer()) {
      return;
    }
    this.gameCount++;
    this.winBalance -= this.eth.edgToEdgwithDec(amount);
    this._lastBet = this.eth.edgToEdgwithDec(amount);
    logger.info('bet', amount, this.winBalance, this.gameCount);
    return this.hashAndSign(pKey);
  }

  /**
   * Adds profit to local state channel data.
   * @param {number} amount
   */
  public addProfit(amount: number): void {
    this.winBalance += this.eth.edgToEdgwithDec(amount);
    logger.info('profit', amount, this.winBalance, this.gameCount);
  }

  /**
   * Ads game to local state channel data.
   * @param {number} game
   */
  public addGame(game: number = 1): void {
    this.gameCount += game;
    logger.info('game', game, this.winBalance, this.gameCount);
  }

  /**
   * Reverts local state-channel data.
   * Useful when bet fails.
   * @param {number} amount
   */
  public revertBet(amount: number = null): void {
    if (this.auth.isTestServer()) {
      return;
    }
    this.gameCount--;
    this.winBalance += amount !== null ? this.eth.edgToEdgwithDec(amount) : this._lastBet;
    logger.info('revert', amount, this.winBalance, this.gameCount);
  }

  /**
   * Hashes state channel data.
   * @returns {string}
   */
  public getMsgHash(): string {
    const msgHash = this.eth.getStateDataHex(this.winBalance, this.gameCount);
    logger.info(`msgHash ${msgHash}`);
    return msgHash;
  }

  /**
   * Signs hashed state channel.
   * @param {string} msgHash
   * @param {Buffer} pKey
   * @param {boolean} binary
   * @returns {Buffer}
   */
  public getSign(msgHash: string, pKey: any = null, binary: boolean = false): any {
    const sign = this.eth.getSign(msgHash, pKey || this.auth.privateKey, binary);
    logger.info('sign', sign);
    return sign;
  }

  /**
   * Hashes state channel and signs it
   * @param {Buffer} pKey
   * @param {boolean} binary
   * @returns {HashAndSign}
   */
  public hashAndSign(pKey: any = null, binary: boolean = false): HashAndSign {
    logger.info('hash and sign');
    const msgHash = this.getMsgHash();
    const sign = this.getSign(msgHash, pKey, binary);
    return {
      msgHash,
      sign,
    };
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Prod_web-client/src/app/core/state-channel.service.ts